import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { useConnections } from '@databases/connections/hooks/useConnections';
import { useDatabaseContent } from '@databases/database-state';
import { Metrics } from '@databases/frontend-metrics';
import { useCallbackEffect } from '@databases/react';
import { appStoreUtils } from '@databases/redux/store';
import { getBridgeAPI } from '@databases/utils/database/bridge';
import { loadDatabaseByReferenceId } from '@databases/utils/redux/databases/actions';
import { selectDatabaseByContentIdStatus } from '@databases/utils/redux/databases/selectors';
import { AsyncOperationStatus } from '@databases/utils/redux/redux';
import { deserializeError } from '@databases/utils/utils';
import React, { FunctionComponent, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl-next';

import { DatabaseActionKey } from '../../../../common/components/database/DatabaseActions';
import DatabaseContent from '../../../../common/components/database-content/DatabaseContent';
import DatabaseContentSkeleton from '../../../../common/components/database-content/DatabaseContentSkeleton';

const actions: DatabaseActionKey[] = [
  'lock',
  'import',
  'print',
  'help',
  'copy',
  'feedback',
];

const DatabaseDetails: FunctionComponent = () => {
  GlobalPageLoadExperience.setPageLoadId(Metrics.STANDALONE_DETAIL_LOAD);
  const { contentId, referenceId, spaceId, spaceKey, spaceName, title } =
    useDatabaseContent();
  useConnections();
  const dispatch = appStoreUtils.useDispatch();
  const intl = useIntl();
  useCallbackEffect(
    useCallback(async () => {
      if (!contentId || !referenceId || !spaceId || !spaceKey) {
        return;
      }

      GlobalPageLoadExperience.mark('database-load-start');
      await dispatch(
        loadDatabaseByReferenceId(
          {
            contentId,
            referenceId,
            spaceId: Number(spaceId),
            spaceKey,
            intl,
            spaceName: spaceName ?? '',
            title: title ?? '',
          },
          { throttle: false }
        )
      );
    }, [contentId, dispatch, referenceId, spaceId, spaceKey, spaceName, title, intl])
  );

  const databaseStatus = appStoreUtils.useSelector((state) =>
    contentId ? selectDatabaseByContentIdStatus(state, { contentId }) : undefined
  );

  useEffect(() => {
    const markBridgeLoadSucceeded = async () => {
      GlobalPageLoadExperience.mark('database-load-end');
      GlobalPageLoadExperience.success({ force: true });
      (await getBridgeAPI()).onContentLoadSucceeded?.({});
    };
    if (databaseStatus?.status === AsyncOperationStatus.SUCCEEDED) {
      markBridgeLoadSucceeded();
    }
  }, [databaseStatus]);

  switch (databaseStatus?.status) {
    case AsyncOperationStatus.ERROR: {
      const error = deserializeError(databaseStatus.error);
      const formattedError = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
      GlobalPageLoadExperience.failure({
        metadata: { error: formattedError },
        force: true,
      });

      throw error;
    }
    case AsyncOperationStatus.SUCCEEDED:
      if (!contentId) {
        throw new Error('loaded db but no contentId?');
      }

      return (
        <DatabaseContent
          contentId={contentId}
          isContentEditable
          isFieldsEditable
          actions={actions}
        />
      );
    default:
      return <DatabaseContentSkeleton />;
  }
};

export default DatabaseDetails;
